import { FC, useMemo } from 'react';
import { BaseChartTemplate } from './BaseChartTemplate';
import type { FinancialHealthStats } from '../../types/financialStats';

// Constants for styling
const LINE_STYLES = {
  CURVED: 0.4
};

interface GrossIncomeChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  onExpandClick?: React.MutableRefObject<any>;
  monthsToShow?: number;
}

// Helper function to determine momentum status
const getMomentumStatus = (monthlyChange: number) => {
  if (monthlyChange > 1000) return { icon: '🚀', label: 'Strong Growth', direction: 'up' };
  if (monthlyChange > 0) return { icon: '📈', label: 'Growing', direction: 'up' };
  if (monthlyChange < -1000) return { icon: '⚠️', label: 'Significant Drop', direction: 'down' };
  if (monthlyChange < 0) return { icon: '📉', label: 'Declining', direction: 'down' };
  return { icon: '→', label: 'Stable', direction: 'stable' };
};

// Helper function to calculate trend line using linear regression
const calculateTrendLine = (data: number[]): number[] => {
  if (data.length <= 2) return data;
  
  // Use linear regression to calculate trend line
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  const n = data.length;
  
  // Calculate sums for linear regression formula
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += data[i];
    sumXY += i * data[i];
    sumXX += i * i;
  }
  
  // Calculate slope and y-intercept
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  // Generate trend line points
  return data.map((_, i) => intercept + slope * i);
};

export const GrossIncomeChart: FC<GrossIncomeChartProps> = ({ 
  data, 
  showDebug, 
  onExpandClick,
  monthsToShow = 12
}) => {
  // Format currency for Y-axis
  const formatCurrency = (value: number) => {
    if (value === 0) return '$0';
    // Always display in $k units for consistency
    return `$${(value/1000).toFixed(1)}k`;
  };
  
  // Format tooltip values
  // VS-BUG-001: Renamed from "Gross Income" to "Deposited Income" for audit accuracy
  const formatTooltip = (value: number) => [`$${value.toLocaleString()}`, 'Deposited Income'];
  
  // Calculate maximum for Y-axis (20% above the max value, rounded to nearest $1000)
  const calculateYMax = useMemo(() => {
    if (data.length === 0) return 5000;
    
    const maxValue = Math.max(...data.map(item => item.gross_income || 0));
    if (maxValue === 0) return 5000;
    
    // Add 20% buffer and round up to nearest $1000
    const withBuffer = maxValue * 1.2;
    return Math.ceil(withBuffer / 1000) * 1000;
  }, [data]);
  
  // Find key data points for showing markers on the chart
  useMemo(() => {
    if (!data || data.length <= 1) return [0];
    
    const points: number[] = [];
    const sortedData = [...data].sort((a, b) => 
      new Date(a.month).getTime() - new Date(b.month).getTime()
    );
    
    // Always include first and last points
    points.push(0);
    points.push(sortedData.length - 1);
    
    // Include local peaks and valleys
    for (let i = 1; i < sortedData.length - 1; i++) {
      const prev = sortedData[i-1].gross_income || 0;
      const curr = sortedData[i].gross_income || 0;
      const next = sortedData[i+1].gross_income || 0;
      
      // Local maximum
      if (curr > prev && curr > next) {
        points.push(i);
      }
      // Local minimum
      else if (curr < prev && curr < next) {
        points.push(i);
      }
      // Significant change
      else if (Math.abs(curr - prev) > 500 || Math.abs(next - curr) > 500) {
        points.push(i);
      }
    }
    
    // Add quarterly points approximately
    for (let i = 3; i < sortedData.length; i += 3) {
      if (!points.includes(i)) {
        points.push(i);
      }
    }
    
    return points;
  }, [data]);
  
  // Prepare chart datasets
  const getChartDatasets = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Sort data chronologically
    const sortedData = [...data].sort((a, b) => 
      new Date(a.month).getTime() - new Date(b.month).getTime()
    );
    
    // Get the most recent months up to monthsToShow
    const visibleData = sortedData.slice(-monthsToShow);
    
    // Extract gross_income values
    const values = visibleData.map(d => d.gross_income || 0);
    
    // Calculate trend line
    const trendLine = calculateTrendLine(values);
    
    return [
      {
        // Main data line
        label: 'Gross Income',
        data: values,
        fill: true,
        tension: LINE_STYLES.CURVED,
        borderWidth: 2.5,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointBorderColor: 'rgba(14, 165, 233, 1)', // sky-500
        pointBorderWidth: 2,
        borderColor: '#0EA5E9', // sky-500
        backgroundColor: 'rgba(14, 165, 233, 0.1)',
      },
      {
        // Trend line
        label: 'Trend',
        data: trendLine,
        fill: false,
        tension: 0.2,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        borderColor: 'rgba(107, 114, 128, 0.8)',
        backgroundColor: 'transparent',
      }
    ];
  }, [data, monthsToShow]);
  
  // Render the overlay with current data
  const renderOverlay = (visibleData: FinancialHealthStats[]) => {
    // Calculate metrics for the overlay
    const currentIncome = visibleData[visibleData.length - 1]?.gross_income || 0;
    const previousIncome = visibleData[visibleData.length - 2]?.gross_income || 0;
    const monthlyChange = currentIncome - previousIncome;
    const momentumStatus = getMomentumStatus(monthlyChange);

    return (
      <div 
        className="absolute left-16 top-2 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-lg border border-slate-200 shadow-sm"
        style={{ zIndex: 1 }}
      >
        <div className="grid gap-4">
          {/* Current Income */}
          <div>
            <div className="text-xs text-slate-500 mb-1">Current Income</div>
            <div className="flex items-center">
              <div className="text-3xl font-bold text-brand-blue">
                ${currentIncome.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
            </div>
          </div>

          {/* Monthly Change & Momentum */}
          <div className="flex items-center justify-between gap-8 border-t border-slate-100 pt-3">
            <div>
              <div className="text-xs text-slate-500">Monthly Change</div>
              <div className="flex items-center gap-2">
                {monthlyChange === 0 ? (
                  <div className="text-sm font-medium">$0.00</div>
                ) : (
                  <>
                    <div
                      className="text-2xl font-semibold"
                      style={{ color: monthlyChange >= 0 ? '#10B981' : '#EF4444' }}
                    >
                      {monthlyChange >= 0 ? '↑' : '↓'}
                    </div>
                    <div className="text-sm font-medium">${Math.abs(monthlyChange).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-500">Momentum</div>
              <div className="flex items-center gap-2">
                {momentumStatus.direction === 'stable' ? (
                  <div className="text-sm flex items-center">
                    <span className="text-lg">→</span>
                    <span className="ml-1">Stable</span>
                  </div>
                ) : (
                  <div className="text-sm font-medium">
                    {momentumStatus.label}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full">
      <BaseChartTemplate
        data={data}
        showDebug={showDebug}
        monthsToShow={monthsToShow}
        onExpandClick={onExpandClick}
        title="Deposited Income"
        dataKey="gross_income"
        color="#0EA5E9" // sky-500
        yAxisFormatter={formatCurrency}
        tooltipFormatter={formatTooltip}
        renderOverlay={renderOverlay}
        max={calculateYMax}
        customDatasets={getChartDatasets}
        yAxisStepSize={1000} // Ensure steps of $1k
        isCurrentFinances={true} // Enable drag functionality
      />
    </div>
  );
};
